import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { toast } from 'sonner';

export interface MarkingNotification {
  id: string;
  company_id: string;
  driver_id: string;
  vehicle_id?: string;
  marking_type: 'fuel' | 'odometer';
  marking_id: string;
  created_at: string;
  read_by: string[];
  metadata: {
    source?: string;
    location?: any;
    [key: string]: any;
  };
  fleet_drivers?: {
    name: string;
    cpf: string;
  };
  fleet_vehicles?: {
    plate: string;
    make?: string;
    model?: string;
  };
}

// Hook to fetch unread notifications
export function useUnreadMarkingNotifications(companyId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['unread-marking-notifications', companyId, user?.id],
    queryFn: async () => {
      if (!companyId || !user?.id) return [];

      const { data, error } = await supabase
        .from('fleet_marking_notifications')
        .select(`
          *,
          fleet_drivers(name, cpf),
          fleet_vehicles(plate, make, model)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Filter only unread by current user
      return (data || []).filter(
        (notif: any) => {
          const readBy = Array.isArray(notif.read_by) ? notif.read_by : [];
          return !readBy.includes(user.id);
        }
      ) as MarkingNotification[];
    },
    enabled: !!companyId && !!user?.id,
    refetchInterval: 10000, // Refresh every 10 seconds
  });
}

// Hook to fetch all notifications
export function useMarkingNotifications(companyId?: string, limit = 100) {
  return useQuery({
    queryKey: ['marking-notifications', companyId, limit],
    queryFn: async () => {
      if (!companyId) return [];

      const { data, error } = await supabase
        .from('fleet_marking_notifications')
        .select(`
          *,
          fleet_drivers(name, cpf),
          fleet_vehicles(plate, make, model)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data || []) as MarkingNotification[];
    },
    enabled: !!companyId,
  });
}

// Hook to mark notification as read
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Fetch current notification
      const { data: current, error: fetchError } = await supabase
        .from('fleet_marking_notifications')
        .select('read_by')
        .eq('id', notificationId)
        .single();

      if (fetchError) throw fetchError;

      const readBy = Array.isArray(current?.read_by) ? current.read_by : [];
      if (!readBy.includes(user.id)) {
        readBy.push(user.id);
      }

      const { error } = await supabase
        .from('fleet_marking_notifications')
        .update({ read_by: readBy })
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unread-marking-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['marking-notifications'] });
    },
  });
}

// Hook to mark all notifications as read
export function useMarkAllNotificationsAsRead(companyId?: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!companyId || !user?.id) throw new Error('Missing required data');

      // Fetch all unread
      const { data: unread, error: fetchError } = await supabase
        .from('fleet_marking_notifications')
        .select('id, read_by')
        .eq('company_id', companyId);

      if (fetchError) throw fetchError;

      // Update each one
      const updates = (unread || [])
        .filter((n: any) => {
          const readBy = Array.isArray(n.read_by) ? n.read_by : [];
          return !readBy.includes(user.id);
        })
        .map((n: any) => {
          const readBy = Array.isArray(n.read_by) ? n.read_by : [];
          readBy.push(user.id);
          return supabase
            .from('fleet_marking_notifications')
            .update({ read_by: readBy })
            .eq('id', n.id);
        });

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unread-marking-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['marking-notifications'] });
      toast.success('Todas as notificações foram marcadas como lidas');
    },
    onError: (error) => {
      toast.error('Erro ao marcar notificações como lidas');
      console.error('Error marking all as read:', error);
    },
  });
}
